
/**
 * Centralized utility for date and time handling in America/Los_Angeles timezone.
 */

/**
 * Formats a UTC date string to Los Angeles time (Medium date, Short time).
 * Example: "Feb 16, 2026, 10:00 AM"
 * @param {string|Date} utcDateString 
 * @returns {string}
 */
export const utcToLATime = (utcDateString) => {
    if (!utcDateString) return "";
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Los_Angeles',
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(new Date(utcDateString));
    } catch (e) {
        console.error("Error formatting LA time:", e);
        return "";
    }
};

/**
 * Returns a detailed timestamp in America/Los_Angeles timezone.
 * Format: MM/DD/YYYY HH:MM:SS AM/PM PST
 * @returns {string}
 */
export const getLATimestamp = () => {
    const now = new Date();
    const pstDate = new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));

    const month = String(pstDate.getMonth() + 1).padStart(2, '0');
    const day = String(pstDate.getDate()).padStart(2, '0');
    const year = pstDate.getFullYear();

    let hours = pstDate.getHours();
    const minutes = String(pstDate.getMinutes()).padStart(2, '0');
    const seconds = String(pstDate.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours || 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');

    return `${month}/${day}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm} PST`;
};

/**
 * Formats an electronic signature with a Los Angeles timestamp.
 * Format: "LASTNAME, FIRSTNAME MM/DD/YYYY HH:MM:SS AM/PM PST"
 * @param {string} name - Full name to format
 * @returns {string}
 */
export const formatSignature = (name) => {
    if (!name) return "";

    const timestamp = getLATimestamp();
    const nameParts = name.trim().split(/\s+/);
    let lastName = "";
    let firstName = "";

    if (nameParts.length === 1) {
        // Only one name provided, use as last name
        lastName = nameParts[0].toUpperCase();
    } else if (nameParts.length >= 2) {
        // Last part is last name, everything else is first name
        lastName = nameParts[nameParts.length - 1].toUpperCase();
        firstName = nameParts.slice(0, -1).map(part => part.toUpperCase()).join(" ");
    }

    if (firstName) {
        return `${lastName}, ${firstName} ${timestamp}`;
    } else {
        return `${lastName} ${timestamp}`;
    }
};

/**
 * Gets the current date in YYYY-MM-DD format based on local system time.
 * @returns {string}
 */
export const getLocalSystemDate = () => {
    // Return YYYY-MM-DD in America/Los_Angeles timezone
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(new Date());
};


/**
 * Compatibility alias for getLocalSystemDate.
 * Many files import this, so we keep it to prevent build errors.
 * Updates it to use local system time as per user request.
 */
export const getCurrentLADate = getLocalSystemDate;

/**
 * Formats a date value for DISPLAY as MM-DD-YYYY.
 * Accepts: YYYY-MM-DD strings, ISO strings, or Date objects.
 * Returns "" for null/undefined/invalid input.
 *
 * To change the display format for the entire app in the future,
 * update only this function.
 *
 * @param {string|Date} date
 * @returns {string} e.g. "02-25-2026"
 */
export const displayDate = (date) => {
    if (!date) return "";
    try {
        let dateStr = "";
        if (date instanceof Date) {
            if (Number.isNaN(date.getTime())) return "";
            dateStr = date.toISOString().split("T")[0];
        } else {
            // Check if it's already MM-DD-YYYY
            const mdyMatch = String(date).match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
            if (mdyMatch) return `${mdyMatch[1]}-${mdyMatch[2]}-${mdyMatch[3]}`;

            // Check if it's YYYY-MM-DD or ISO
            const ymdMatch = String(date).match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
            if (ymdMatch) {
                dateStr = `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
            } else {
                // Try parsing as generic date
                const dt = new Date(date);
                if (Number.isNaN(dt.getTime())) return "";
                dateStr = dt.toISOString().split("T")[0];
            }
        }

        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${month}-${day}-${year}`;
    } catch (e) {
        console.error("displayDate error:", e);
        return "";
    }
};

/**
 * Formats a date for display as MM/DD/YYYY (slash-separated US style).
 * Delegates to {@link displayDate}; use this when you need slashes instead of hyphens.
 *
 * @param {string|Date} date
 * @returns {string} e.g. "02/25/2026", or "" if invalid/empty
 */
export const displayDateUS = (date) => {
    const d = displayDate(date);
    return d ? d.replace(/-/g, "/") : "";
};

/**
 * Formats a time value for display as h:mm AM/PM (America/Los_Angeles).
 * Used by Activity / Payroll time-card Clock in, Clock out, and Breaks.
 *
 * @param {string|Date|null|undefined} date
 * @returns {string} e.g. "10:40 AM", or "—" if invalid/empty
 */
export const formatTimeAmPm = (date) => {
    if (!date) return "—";
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(dateObj.getTime())) return "—";
        return new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }).format(dateObj);
    } catch {
        return "—";
    }
};

/**
 * Formats date + time for display as MM/DD/YYYY h:mm AM/PM (America/Los_Angeles).
 *
 * @param {string|Date} date
 * @returns {string} e.g. "07/24/2026 2:35 PM", or "" if invalid/empty
 */
export const displayDateTimeUS = (date) => {
    if (!date) return "";
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(dateObj.getTime())) return "";
        const datePart = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        }).format(dateObj);
        const timePart = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }).format(dateObj);
        return `${datePart} ${timePart}`;
    } catch {
        return "";
    }
};

/**
 * Formats a date value for an input[type="date"] string.
 * Ensures the output is YYYY-MM-DD in America/Los_Angeles timezone.
 * @param {string|Date} val
 * @returns {string} e.g. "2026-02-25"
 */
export const formatDateForInput = (val) => {
    if (!val) return "";
    try {
        if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return val;
        }
        // If it's MM-DD-YYYY, convert to YYYY-MM-DD
        const mdy = String(val).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
        if (mdy) {
            return `${mdy[3]}-${mdy[1]}-${mdy[2]}`;
        }

        const dateObj = val instanceof Date ? val : new Date(val);
        if (Number.isNaN(dateObj.getTime())) return "";

        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(dateObj);
    } catch {
        return "";
    }
};

/**
 * Helper to convert YYYY-MM-DD or other date strings to ISO 8601 (e.g., 2026-02-01T00:00:00.000Z).
 * Useful for ensuring standard date formats in API payloads.
 * @param {string} dateString
 * @returns {string|null}
 */
export const toIsoDate = (dateString) => {
    if (dateString == null || dateString === "") return "";
    try {
        if (dateString instanceof Date) {
            return Number.isNaN(dateString.getTime()) ? "" : dateString.toISOString();
        }
        const raw = String(dateString).trim();
        if (!raw) return "";
        if (/^\d{4}-\d{2}-\d{2}T.+(Z|[+-]\d{2}:\d{2})$/.test(raw)) return raw;
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00:00.000Z`;
        const mdy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
        if (mdy) return `${mdy[3]}-${mdy[1]}-${mdy[2]}T00:00:00.000Z`;
        const dt = new Date(raw);
        return Number.isNaN(dt.getTime()) ? "" : dt.toISOString();
    } catch {
        return "";
    }
};

/**
 * Helper to safely extract MM-DD-YYYY from an ISO date string or valid Date string.
 * This ensures that strings with timezone information map back to input fields properly.
 * @param {string} isoString
 * @returns {string} 
 */
export const parseIsoDateToMDY = (isoString) => {
    if (!isoString) return "";
    try {
        const dt = new Date(isoString);
        if (Number.isNaN(dt.getTime())) return "";

        const yyyymmdd = dt.toISOString().split('T')[0];
        const [year, month, day] = yyyymmdd.split('-');
        return `${month}-${day}-${year}`;
    } catch {
        return "";
    }
};

/**
 * Same calendar date as {@link parseIsoDateToMDY}; display only uses slashes (MM/DD/YYYY).
 * @param {string} isoString
 * @returns {string}
 */
export const parseIsoDateToMDYUS = (isoString) => {
    const d = parseIsoDateToMDY(isoString);
    return d ? d.replace(/-/g, "/") : "";
};

/**
 * Formats an ISO date string to LA timestamp for e-signature display.
 * Format: "MM/DD/YYYY HH:MM:SS AM/PM PST"
 * @param {string|Date} isoString
 * @returns {string}
 */
export const formatLATimestampForDate = (isoString) => {
  if (!isoString) return "";
  try {
    const dt = new Date(isoString);
    if (Number.isNaN(dt.getTime())) return "";
    const dateStr = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(dt);
    const timeStr = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(dt);
    return `${dateStr} ${timeStr} PST`;
  } catch {
    return "";
  }
};

/**
 * Formats a signed-at timestamp for e-signature display.
 * Format: "03:45 PM, 03-15-2024"
 * @param {string|Date} isoString
 * @returns {string}
 */
export const formatSignedTimestamp = (isoString) => {
  if (!isoString) return "";
  try {
    const dt = new Date(isoString);
    if (Number.isNaN(dt.getTime())) return "";
    const timeStr = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/Los_Angeles",
    }).format(dt);
    const dateStr = parseIsoDateToMDY(isoString);
    return `${timeStr}, ${dateStr}`;
  } catch {
    return "";
  }
};

/**
 * Checks if a date is expired relative to the current time in America/Los_Angeles.
 * @param {string|Date} expireAt 
 * @returns {boolean}
 */
export const isDateExpired = (expireAt) => {
    if (!expireAt) return false;
    try {
        const laNowStr = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(new Date());

        const expireDateStr = formatDateForInput(expireAt);
        return expireDateStr < laNowStr;
    } catch {
        return false;
    }
};

/** Attendance wall-clock timezone (must match backend ATTENDANCE_TIMEZONE). */
export const ATTENDANCE_TIMEZONE = "America/Los_Angeles";

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Convert a UTC ISO timestamp to a datetime-local string (YYYY-MM-DDTHH:mm)
 * in America/Los_Angeles. Minutes are taken from the LA clock (seconds dropped,
 * not rounded) so edit fields match the time-card display.
 */
export const utcToAttendanceLocalDateTime = (isoString) => {
    if (!isoString) return "";
    const date = isoString instanceof Date ? isoString : new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: ATTENDANCE_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "00";
    let hour = get("hour");
    if (hour === "24") hour = "00";
    return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
};

/** YYYY-MM-DD from a datetime-local or YYYY-MM-DD string. */
export const attendanceDatePart = (val) => {
    if (!val || typeof val !== "string") return "";
    const s = val.trim();
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return "";
};

/** Keep time-of-day, force the attendance calendar date (edit must not change date). */
export const withLockedAttendanceDate = (localDateTime, lockedDate) => {
    if (!lockedDate) return localDateTime || "";
    if (!localDateTime || typeof localDateTime !== "string") {
        return `${lockedDate}T09:00`;
    }
    const timePart = localDateTime.includes("T")
        ? localDateTime.split("T")[1]
        : "09:00";
    const hhmm = timePart.slice(0, 5);
    return `${lockedDate}T${hhmm}`;
};

/**
 * Parse YYYY-MM-DDTHH:mm as a civil Date (browser local components = wall clock).
 * Does not convert timezones — the string is already attendance TZ wall-clock.
 */
export const parseCivilDateTime = (val) => {
    if (!val) return null;
    const m = String(val).trim().match(
        /^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/,
    );
    if (m) {
        return new Date(
            Number(m[1]),
            Number(m[2]) - 1,
            Number(m[3]),
            Number(m[4]),
            Number(m[5]),
            0,
            0,
        );
    }
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** Format a Date's local Y-M-D H:m as YYYY-MM-DDTHH:mm (civil wall clock). */
export const formatCivilDateTime = (date) => {
    if (!date || Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/** Ensure seconds for API: YYYY-MM-DDTHH:mm:00 */
export const toAttendancePayloadDateTime = (val) => {
    if (!val || typeof val !== "string") return null;
    const s = val.trim();
    if (!s) return null;
    return s.length === 16 ? `${s}:00` : s;
};
